import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { SignatureEnvelope } from '../models/SignatureEnvelope';
import { useAuth } from '../contexts/AuthContext';
import { logError } from '../../lib/utils';

export function useEnvelopes(organizationId?: string | null) {
  const [envelopes, setEnvelopes] = useState<SignatureEnvelope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'signatureEnvelopes'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const envelopesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            envelopeId: doc.id,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            // Convert Timestamps in nested objects
            clientSigner: {
              ...data.clientSigner,
              signedAt: data.clientSigner?.signedAt?.toDate(),
              signingDate: data.clientSigner?.signingDate?.toDate(),
            },
            companySigner: {
              ...data.companySigner,
              signedAt: data.companySigner?.signedAt?.toDate(),
              signingDate: data.companySigner?.signingDate?.toDate(),
            },
            tokenExpiresAt: data.tokenExpiresAt?.toDate(),
            auditLog: (data.auditLog || []).map((entry: any) => ({
              ...entry,
              timestamp: entry.timestamp?.toDate() || new Date(),
            })),
          } as SignatureEnvelope;
        });

        setEnvelopes(envelopesData);
        setLoading(false);
        setError(null);
      },
      (err: unknown) => {
        logError('signatures', err, { source: 'useEnvelopes', organizationId });
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId, currentUser?.role]);

  return {
    envelopes,
    loading,
    error,
  };
}
